// Order timing per bar shift: how long from a QR order to delivered, against the 25-min promise.
import React, { useCallback, useEffect, useState } from 'react';
import { Timer, RefreshCw } from 'lucide-react';
import { apiCall } from '../_shared';

const FLAG = {
  ok: { cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', text: 'On time' },
  watch: { cls: 'bg-amber-50 text-amber-800 border-amber-200', text: 'Watch' },
  behind: { cls: 'bg-red-50 text-red-800 border-red-200', text: 'Falling behind' },
  no_data: { cls: 'bg-slate-50 text-slate-500 border-slate-200', text: 'No deliveries yet' },
};
const fmt = (m) => (m == null ? '—' : `${m} min`);

const ShiftRow = ({ s, target }) => {
  const f = FLAG[s.flag] || FLAG.no_data;
  const width = s.avg_min == null ? 0 : Math.min(100, Math.round((s.avg_min / (target * 2)) * 100));
  return (
    <div className="border-t py-2" data-testid={`timing-shift-${s.shift}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-slate-900 w-36">{s.label}</span>
        <span className={`border rounded px-1.5 py-0.5 text-[11px] ${f.cls}`} data-testid={`timing-flag-${s.shift}`}>{f.text}</span>
        <span className="text-slate-500">{s.orders} orders · {s.delivered} delivered</span>
        {s.late_pct != null && <span className={`ml-auto font-semibold ${s.late_pct >= 30 ? 'text-red-700' : s.late_pct >= 15 ? 'text-amber-700' : 'text-emerald-700'}`}>{s.late_pct}% over {target} min</span>}
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-slate-100 relative overflow-hidden">
        <div className={`h-full rounded-full ${s.flag === 'behind' ? 'bg-red-500' : s.flag === 'watch' ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${width}%` }} />
        <div className="absolute top-0 bottom-0 w-px bg-slate-700" style={{ left: '50%' }} title={`${target} min target`} />
      </div>
      <p className="text-[11px] text-slate-500 mt-1">avg <b data-testid={`timing-avg-${s.shift}`}>{fmt(s.avg_min)}</b> · median {fmt(s.median_min)} · slowest 10% {fmt(s.p90_min)} · to prepared {fmt(s.avg_to_prepared_min)}</p>
    </div>
  );
};

export const OrderTiming = () => {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { setData(await apiCall(`/api/admin/inroom/orders/timing?days=${days}`)); setErr(''); } catch (e) { setErr(e.message); }
  }, [days]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  if (err) return <p className="text-xs text-red-600" data-testid="timing-error">{err}</p>;
  if (!data) return null;
  const shiftRows = (data.shifts || []).map((s) => <ShiftRow key={s.shift} s={s} target={data.target_min} />);
  const slowList = data.slowest || [];
  const slow = slowList.map((o) => <li key={o.id} className="flex gap-2"><span className="font-semibold text-red-700 w-16">{o.minutes} min</span><span>{String(o.created_at).slice(5, 16).replace('T', ' ')} · {o.unit_name ? `Room ${o.unit_name}` : o.room_name} · {o.items} item(s){o.handled_by ? ` · ${o.handled_by}` : ''}</span></li>);
  const openList = data.open || [];
  const waiting = openList.filter((o) => o.waiting_min > data.target_min);
  const o = data.overall;
  return (
    <details className="bg-white rounded-xl border p-3 mt-4" data-testid="order-timing" open>
      <summary className="cursor-pointer text-sm font-semibold text-slate-900 flex flex-wrap items-center gap-2">
        <Timer className="w-4 h-4 text-orange-600" /> Order timing · last {data.days} days
        {o.avg_min != null && <span className="text-xs font-normal text-slate-500" data-testid="timing-overall">avg {o.avg_min} min · {o.late_pct}% over {data.target_min} min · {o.delivered} delivered</span>}
        {waiting.length > 0 && <span className="text-[11px] bg-red-600 text-white rounded-full px-2 py-0.5 animate-pulse" data-testid="timing-waiting">{waiting.length} waiting &gt; {data.target_min} min now</span>}
        <span className="ml-auto flex items-center gap-1 text-xs font-normal">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border rounded px-1 py-0.5" data-testid="timing-days"><option value={7}>7 days</option><option value={14}>14 days</option><option value={30}>30 days</option></select>
          <button type="button" onClick={load} className="border rounded px-1.5 py-0.5" data-testid="timing-refresh"><RefreshCw className="w-3 h-3" /></button>
        </span>
      </summary>
      <div className="mt-2">{shiftRows}</div>
      {slow.length > 0 && <div className="mt-3 text-[11px] text-slate-600"><p className="font-semibold text-slate-700">Slowest deliveries</p><ul className="mt-1 space-y-0.5" data-testid="timing-slowest">{slow}</ul></div>}
      <p className="text-[10px] text-slate-400 mt-2">Target {data.target_min} min — what the QR page promises guests. Shifts in Podgorica time; cancelled orders excluded.</p>
    </details>
  );
};

export default OrderTiming;
